export type GuideId =
  | 'android-wg'
  | 'android-ovpn'
  | 'ios-wg'
  | 'ios-ovpn'
  | 'windows-wg'
  | 'windows-ovpn'
  | 'macos-wg'
  | 'macos-ovpn';

export type DeviceKind = 'phone' | 'computer';
export type OsId = 'android' | 'ios' | 'windows' | 'macos';
export type ProtocolId = 'wg' | 'ovpn';

export interface GuideStep {
  title: string;
  body: string[];
  tip?: string;
  link?: { label: string; href: string };
  actionLabel?: string;
}

export interface Guide {
  id: GuideId;
  title: string;
  device: DeviceKind;
  os: OsId;
  protocol: ProtocolId;
  duration: string;
  needs: string[];
  steps: GuideStep[];
}

export interface ProblemArticle {
  id: string;
  title: string;
  summary: string;
  causes: string[];
  steps: string[];
}

export interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

const WG_PLAY = 'https://play.google.com/store/apps/details?id=com.wireguard.android';
const OVPN_PLAY = 'https://play.google.com/store/apps/details?id=net.openvpn.openvpn';
const WG_APPSTORE = 'https://apps.apple.com/app/wireguard/id1441195209';
const OVPN_APPSTORE = 'https://apps.apple.com/app/openvpn-connect/id590379981';
const WG_DESKTOP = 'https://www.wireguard.com/install/';
const OVPN_DESKTOP = 'https://openvpn.net/client/';

export const GUIDES: Guide[] = [
  {
    id: 'android-wg',
    title: 'Android · WireGuard',
    device: 'phone',
    os: 'android',
    protocol: 'wg',
    duration: 'около 1 минуты',
    needs: ['Интернет', 'Telegram или браузер', 'WireGuard'],
    steps: [
      {
        title: 'Установите WireGuard',
        body: ['Установите приложение WireGuard из Google Play.'],
        link: { label: 'Открыть Google Play', href: WG_PLAY },
        actionLabel: 'Уже установлено',
      },
      {
        title: 'Скачайте конфигурацию',
        body: [
          'Вернитесь в личный кабинет во вкладку «Ключи».',
          'Нажмите «Конфиг» или «Скачать» у нужного ключа.',
          'Будет загружен файл .conf — не изменяйте и не переименовывайте его.',
        ],
        tip: 'Файл .conf содержит параметры подключения.',
      },
      {
        title: 'Где искать файл?',
        body: [
          'Чаще всего Android сохраняет файл в папку Download / Downloads или Telegram.',
          'Откройте приложение «Файлы» → Внутренняя память → Download.',
        ],
        tip: 'Если файла нет — скачайте конфигурацию ещё раз и дождитесь окончания загрузки.',
      },
      {
        title: 'Импортируйте туннель',
        body: [
          'Откройте WireGuard.',
          'Нажмите «+».',
          'Выберите «Импорт из файла» / «Import from file or archive».',
          'Найдите и выберите скачанный .conf.',
        ],
      },
      {
        title: 'Подтвердите системный запрос',
        body: [
          'Android покажет запрос на создание сетевого профиля.',
          'Нажмите «Разрешить» / «OK».',
        ],
      },
      {
        title: 'Подключитесь',
        body: [
          'Включите переключатель рядом с туннелем.',
          'Если появился обмен трафиком — настройка завершена.',
        ],
      },
    ],
  },
  {
    id: 'android-ovpn',
    title: 'Android · OpenVPN',
    device: 'phone',
    os: 'android',
    protocol: 'ovpn',
    duration: 'около 1 минуты',
    needs: ['Интернет', 'Telegram или браузер', 'OpenVPN Connect'],
    steps: [
      {
        title: 'Установите OpenVPN Connect',
        body: ['Установите OpenVPN Connect из Google Play.'],
        link: { label: 'Открыть Google Play', href: OVPN_PLAY },
        actionLabel: 'Уже установлено',
      },
      {
        title: 'Скачайте конфигурацию',
        body: [
          'В кабинете откройте «Ключи» → OpenVPN.',
          'Скачайте файл .ovpn.',
          'Не открывайте его текстовым редактором.',
        ],
      },
      {
        title: 'Где искать файл?',
        body: ['Обычно файл лежит в Download / Downloads или в папке Telegram.'],
      },
      {
        title: 'Импортируйте профиль',
        body: [
          'Откройте OpenVPN Connect.',
          'Нажмите Import Profile / Импорт профиля.',
          'Выберите скачанный .ovpn.',
        ],
      },
      {
        title: 'Подключитесь',
        body: [
          'Нажмите Connect.',
          'Подтвердите системный запрос, если появится.',
          'Дождитесь статуса Connected.',
        ],
      },
    ],
  },
  {
    id: 'ios-wg',
    title: 'iPhone · WireGuard',
    device: 'phone',
    os: 'ios',
    protocol: 'wg',
    duration: 'около 1 минуты',
    needs: ['Telegram', 'WireGuard', 'Интернет'],
    steps: [
      {
        title: 'Установите WireGuard',
        body: ['Установите WireGuard из App Store.'],
        link: { label: 'Открыть App Store', href: WG_APPSTORE },
        actionLabel: 'Уже установлено',
      },
      {
        title: 'Скачайте конфигурацию',
        body: [
          'Вернитесь в личный кабинет и нажмите «Скачать» / «Конфиг».',
          'Файл появится в чате Telegram.',
          'Приложение «Файлы» для этого сценария не нужно.',
        ],
      },
      {
        title: 'Откройте файл в Telegram',
        body: ['Нажмите на скачанный файл обычным касанием.', 'Не удерживайте его долго.'],
      },
      {
        title: 'Нажмите «Поделиться»',
        body: ['Кнопка «Поделиться» обычно в левом нижнем углу экрана предпросмотра.'],
      },
      {
        title: 'Выберите WireGuard',
        body: [
          'В списке приложений выберите WireGuard.',
          'Если его нет — нажмите «Ещё», включите WireGuard и повторите.',
        ],
        tip: 'После свежей установки список «Поделиться» иногда обновляется с задержкой.',
      },
      {
        title: 'Добавьте туннель',
        body: ['WireGuard откроется сам.', 'Нажмите «Add Tunnel» / «Добавить туннель» и подтвердите.'],
      },
      {
        title: 'Подтвердите системный запрос',
        body: ['Подтвердите запрос системы.', 'При необходимости используйте Face ID / код-пароль.'],
      },
      {
        title: 'Подключитесь',
        body: ['Включите переключатель у туннеля.', 'Статус «Активно» означает, что всё готово.'],
      },
    ],
  },
  {
    id: 'ios-ovpn',
    title: 'iPhone · OpenVPN',
    device: 'phone',
    os: 'ios',
    protocol: 'ovpn',
    duration: 'около 2 минут',
    needs: ['Кабинет', 'OpenVPN Connect', 'Приложение «Файлы»'],
    steps: [
      {
        title: 'Установите OpenVPN Connect',
        body: ['Установите OpenVPN Connect из App Store.'],
        link: { label: 'Открыть App Store', href: OVPN_APPSTORE },
        actionLabel: 'Уже установлено',
      },
      {
        title: 'Нажмите «Скачать» в кабинете',
        body: [
          'Откройте «Ключи» → OpenVPN → «Скачать».',
          'Откроется меню «Поделиться».',
        ],
      },
      {
        title: 'Сохраните в «Файлы»',
        body: [
          'В меню выберите «Сохранить в Файлы» (Загрузки / На iPhone).',
          'OpenVPN в этом меню обычно нет — так и должно быть.',
        ],
      },
      {
        title: 'Импорт из «Файлов»',
        body: [
          'Откройте приложение «Файлы» → ваш .ovpn.',
          'Поделиться → OpenVPN Connect (при необходимости «Ещё…»).',
        ],
      },
      {
        title: 'Подключение',
        body: ['Подтвердите Import.', 'Нажмите Connect и разрешите VPN-конфигурацию.'],
      },
    ],
  },
  {
    id: 'windows-wg',
    title: 'Windows · WireGuard',
    device: 'computer',
    os: 'windows',
    protocol: 'wg',
    duration: 'около 1 минуты',
    needs: ['Интернет', 'WireGuard', 'Файл .conf'],
    steps: [
      {
        title: 'Установите WireGuard',
        body: ['Скачайте и установите WireGuard с официального сайта.'],
        link: { label: 'Скачать WireGuard', href: WG_DESKTOP },
        actionLabel: 'Уже установлено',
      },
      {
        title: 'Скачайте конфигурацию',
        body: [
          'В кабинете нажмите «Скачать» / «Конфиг».',
          'Не открывайте файл через Блокнот и не переименовывайте его.',
        ],
      },
      {
        title: 'Где искать файл?',
        body: [
          'По умолчанию Windows сохраняет файлы в папку «Загрузки».',
          'Если браузер спрашивал путь — откройте выбранную папку.',
        ],
      },
      {
        title: 'Импортируйте туннель',
        body: [
          'Откройте WireGuard.',
          'Нажмите Import tunnel(s) from file.',
          'Выберите скачанный .conf.',
        ],
      },
      {
        title: 'Активируйте подключение',
        body: ['Нажмите Activate.', 'Зелёный статус означает, что подключение работает.'],
        tip: 'Если туннель уже есть — удалите старый и импортируйте новый файл.',
      },
    ],
  },
  {
    id: 'windows-ovpn',
    title: 'Windows · OpenVPN',
    device: 'computer',
    os: 'windows',
    protocol: 'ovpn',
    duration: 'около 1 минуты',
    needs: ['Интернет', 'OpenVPN Connect', 'Файл .ovpn'],
    steps: [
      {
        title: 'Установите OpenVPN Connect',
        body: ['Установите OpenVPN Connect с официального сайта.'],
        link: { label: 'Скачать OpenVPN', href: OVPN_DESKTOP },
        actionLabel: 'Уже установлено',
      },
      {
        title: 'Скачайте .ovpn',
        body: ['В кабинете скачайте OpenVPN-конфиг.', 'Не открывайте его редактором.'],
      },
      {
        title: 'Импортируйте профиль',
        body: ['Откройте OpenVPN Connect → Import Profile.', 'Выберите файл .ovpn.'],
      },
      {
        title: 'Подключитесь',
        body: ['Нажмите Connect и дождитесь статуса Connected.'],
      },
    ],
  },
  {
    id: 'macos-wg',
    title: 'macOS · WireGuard',
    device: 'computer',
    os: 'macos',
    protocol: 'wg',
    duration: 'около 1 минуты',
    needs: ['Интернет', 'WireGuard', 'Файл .conf'],
    steps: [
      {
        title: 'Установите WireGuard',
        body: ['Установите WireGuard из App Store или с официального сайта.'],
        link: { label: 'Скачать WireGuard', href: WG_DESKTOP },
        actionLabel: 'Уже установлено',
      },
      {
        title: 'Скачайте конфигурацию',
        body: ['В кабинете скачайте .conf.', 'Safari обычно кладёт файл в Downloads.'],
      },
      {
        title: 'Импортируйте туннель',
        body: ['Откройте WireGuard → Import Tunnel(s).', 'Выберите скачанный .conf.'],
      },
      {
        title: 'Активируйте',
        body: ['Нажмите Activate.', 'После зелёного статуса настройка завершена.'],
      },
    ],
  },
  {
    id: 'macos-ovpn',
    title: 'macOS · OpenVPN',
    device: 'computer',
    os: 'macos',
    protocol: 'ovpn',
    duration: 'около 1 минуты',
    needs: ['Интернет', 'OpenVPN Connect', 'Файл .ovpn'],
    steps: [
      {
        title: 'Установите OpenVPN Connect',
        body: ['Установите OpenVPN Connect с официального сайта или App Store.'],
        link: { label: 'Скачать OpenVPN', href: OVPN_DESKTOP },
        actionLabel: 'Уже установлено',
      },
      {
        title: 'Скачайте и импортируйте',
        body: [
          'Скачайте .ovpn в кабинете.',
          'В OpenVPN Connect выберите Import Profile и укажите файл.',
        ],
      },
      {
        title: 'Подключитесь',
        body: ['Нажмите Connect и подтвердите системный запрос, если macOS спросит.'],
      },
    ],
  },
];

export const PROBLEMS: ProblemArticle[] = [
  {
    id: 'no-internet',
    title: 'Профиль включён, но интернет не работает',
    summary: 'Приложение показывает активное подключение, но сайты не открываются.',
    causes: [
      'Нет интернета без профиля',
      'Устаревшая конфигурация',
      'Проблема с DNS или сетью',
      'Временная недоступность сервера',
    ],
    steps: [
      'Выключите профиль и проверьте интернет без него.',
      'Включите профиль снова.',
      'Переключитесь между Wi‑Fi и мобильной сетью.',
      'Перезагрузите устройство.',
      'Скачайте новую конфигурацию и импортируйте её заново.',
    ],
  },
  {
    id: 'download-fail',
    title: 'Не скачивается файл',
    summary: 'Конфигурация не загружается из кабинета или Telegram.',
    causes: ['Нестабильное соединение', 'Сбой браузера или Telegram'],
    steps: [
      'Обновите страницу кабинета или Telegram.',
      'Попробуйте другой браузер / сеть.',
      'Скачайте файл ещё раз и дождитесь окончания загрузки.',
    ],
  },
  {
    id: 'find-file',
    title: 'Не могу найти файл',
    summary: 'Конфиг скачан, но непонятно, где он лежит.',
    causes: ['Файл ушёл в Download / Загрузки / «Файлы»'],
    steps: [
      'Android: Download, Downloads или папка Telegram.',
      'iPhone · OpenVPN: приложение «Файлы» → Загрузки / На iPhone.',
      'iPhone · WireGuard: файл из Telegram → «Поделиться» → WireGuard.',
      'Windows: папка «Загрузки».',
      'macOS: папка Downloads.',
      'Если файла нет — скачайте / подготовьте повторно.',
    ],
  },
  {
    id: 'invalid-key',
    title: 'Invalid private / public key',
    summary: 'WireGuard пишет, что ключ неверный или повреждён.',
    causes: ['Файл повреждён', 'Конфиг открывали и меняли в редакторе'],
    steps: [
      'Удалите старый туннель.',
      'Скачайте новую конфигурацию.',
      'Импортируйте файл повторно, не открывая его в Блокноте.',
    ],
  },
  {
    id: 'tunnel-exists',
    title: 'Tunnel / Interface already exists',
    summary: 'Такой туннель или интерфейс уже есть в приложении.',
    causes: ['Старая конфигурация не удалена'],
    steps: [
      'Удалите существующий туннель / интерфейс.',
      'Импортируйте новый файл заново.',
    ],
  },
  {
    id: 'handshake',
    title: 'Latest Handshake: Never или RX = 0',
    summary: 'Подключение есть, но сервер не отвечает.',
    causes: ['Нет маршрута до сервера', 'Блокировка сети', 'Неверное время на устройстве'],
    steps: [
      'Проверьте интернет без активного профиля.',
      'Проверьте дату и время на устройстве.',
      'Попробуйте другую сеть (мобильный интернет).',
      'Выключите и включите профиль.',
      'Скачайте новую конфигурацию.',
    ],
  },
  {
    id: 'profile-conflict',
    title: 'Не удаётся создать профиль',
    summary: 'Система не даёт включить новый сетевой профиль.',
    causes: [
      'Уже работает другой сетевой профиль',
      'AdGuard / WARP / Amnezia / корпоративный профиль',
    ],
    steps: [
      'Отключите другие приложения с сетевым профилем.',
      'Повторите импорт и подключение.',
      'При необходимости перезагрузите устройство.',
    ],
  },
  {
    id: 'share-missing',
    title: 'Нет OpenVPN / WireGuard в «Поделиться» (iPhone)',
    summary: 'В меню шаринга нет нужного приложения.',
    causes: [
      'Открыли «Поделиться» из Safari / браузера, а не из «Файлов»',
      'Приложение не установлено',
      'Список не обновился',
    ],
    steps: [
      'Для OpenVPN: в меню из кабинета выберите «Сохранить в Файлы», затем из «Файлов» → Поделиться → OpenVPN.',
      'Для WireGuard: делитесь файлом из Telegram-чата.',
      'Убедитесь, что OpenVPN Connect / WireGuard установлены.',
      'В меню «Поделиться» нажмите «Ещё…», включите приложение и «Готово».',
    ],
  },
  {
    id: 'opened-as-text',
    title: 'Файл открылся как текст',
    summary: 'Конфиг открылся в редакторе вместо приложения WireGuard / OpenVPN Connect.',
    causes: ['Файл открыли через просмотрщик Safari или Блокнот'],
    steps: [
      'Закройте редактор, ничего не сохраняя.',
      'iPhone · OpenVPN: снова «Скачать» → «Сохранить в Файлы», затем импорт из «Файлов».',
      'Скачайте конфигурацию заново.',
      'Импортируйте через WireGuard / OpenVPN Connect, а не двойным кликом.',
    ],
  },
  {
    id: 'update-config',
    title: 'Как обновить конфигурацию',
    summary: 'Нужно заменить старый ключ на новый.',
    causes: ['Выдан новый ключ', 'Старый туннель больше не работает'],
    steps: [
      'Удалите старый туннель в приложении.',
      'Скачайте новую конфигурацию в кабинете.',
      'Импортируйте её заново.',
      'Не импортируйте новый файл «поверх» старого.',
    ],
  },
];

export const FAQ_ITEMS: FaqItem[] = [
  {
    id: 'multi-device',
    question: 'Можно ли использовать одну конфигурацию на нескольких устройствах?',
    answer: 'Лучше создать отдельную конфигурацию для каждого устройства.',
  },
  {
    id: 'share-friend',
    question: 'Можно ли отправить .conf другу?',
    answer: 'Нет. Файл содержит персональные параметры подключения.',
  },
  {
    id: 'open-conf',
    question: 'Можно ли открыть .conf?',
    answer: 'Да, но нельзя изменять содержимое. Для подключения файл нужно импортировать в приложение.',
  },
  {
    id: 'phone-and-pc',
    question: 'Можно ли пользоваться на телефоне и компьютере одновременно?',
    answer: 'Да, если для каждого устройства есть своя конфигурация.',
  },
  {
    id: 'speed',
    question: 'Почему скорость ниже ожидаемой?',
    answer:
      'Скорость зависит от качества сети, удалённости сервера, нагрузки и ограничений оператора.',
  },
  {
    id: 'ip-change',
    question: 'Почему изменился IP?',
    answer: 'Используется адрес выбранного сервера. Это нормальное поведение.',
  },
  {
    id: 'new-phone',
    question: 'Что делать после смены телефона?',
    answer: 'Установите приложение, скачайте новую конфигурацию и импортируйте её.',
  },
  {
    id: 'delete-profile',
    question: 'Как удалить профиль?',
    answer: 'Удалите туннель в WireGuard или профиль в OpenVPN Connect.',
  },
];

export function getGuide(id: GuideId): Guide | undefined {
  return GUIDES.find((g) => g.id === id);
}

export function guidesForOs(os: OsId): Guide[] {
  return GUIDES.filter((g) => g.os === os);
}

export function getProblem(id: string): ProblemArticle | undefined {
  return PROBLEMS.find((p) => p.id === id);
}

export const OS_META: Record<
  OsId,
  { label: string; device: DeviceKind; blurb: string }
> = {
  android: { label: 'Android', device: 'phone', blurb: 'Google Play и папка Download' },
  ios: { label: 'iPhone', device: 'phone', blurb: 'OpenVPN через «Файлы»; WireGuard через Telegram' },
  windows: { label: 'Windows', device: 'computer', blurb: 'Папка «Загрузки»' },
  macos: { label: 'macOS', device: 'computer', blurb: 'Папка Downloads' },
};
